'use client';

import { swissCantonLabel } from '@/lib/countries';
import { useCallback, useMemo, useState } from 'react';
import { createFormatters } from '@/new_components/company-detail/currencyFormatters';
import { ColumnDef, RowSelectionState, VisibilityState } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LegalStatusBadge } from './LegalStatusBadge';
import { companyCountryCode, generateCompanySlug } from '@/lib/company-utils';
import { cn } from '@/lib/utils';
import { CompanyCountryFlag } from '@/new_components/shared/CompanyCountryFlag';
import { useSettings } from '@/providers/settings-provider';
import { useLocale } from '@/hooks/use-locale';
import { Checkbox } from '@/components/ui/checkbox';
import { backfillEmployeesBatchAction, backfillWebsiteBatchAction } from '@/lib/actions/backfill';
import { CompanySearchResult } from '@/lib/actions/company-search';
import { ExportDialog, exportToExcel } from './ResultsExportDialog';
import { SortIcon, TableImplementation } from './ResultsTableParts';
import { DEFAULT_COLUMN_VISIBILITY, MOBILE_PAGE_SIZE, SWISS_DEFAULT_COLUMN_VISIBILITY, VISIBLE_COLUMNS_SETTING, VISIBLE_COLUMNS_SETTING_AT, VISIBLE_COLUMNS_SETTING_CH, columnLabelKey, numberFormatter, secondaryTextClass } from './resultsTableColumns';
import { ProFeatureDialog } from './ProFeatureDialog';
import { MobileResultsList } from './MobileResultsList';
import { ResultsToolbar } from './ResultsToolbar';
import { CompanyNameLink } from './CompanyNameLink';
import { SelectAllCheckbox } from './SelectAllCheckbox';

interface CompanyResultsTableProps {
  previewEuId?: string | null;
  onOpenPreview?: (euId: string, trigger?: HTMLElement) => void;
  results: CompanySearchResult[];
  total: number;
  totalCapped?: boolean;
  offset: number;
  limit?: number;
  sort?: string;
  financialYear?: number;
  // Whether the user may export (Pro or Admin). The export button is shown to
  // everyone; non-entitled users get a "Pro feature" prompt instead.
  canExport?: boolean;
  // Whether the user may run the enrich (website/employee) batch actions —
  // Pro or Admin. The buttons are hidden entirely for everyone else.
  canEnrich?: boolean;
  // Whether the user may customize visible columns (Pro or Admin). The Columns
  // button is shown to everyone; non-entitled users get a "Pro feature" prompt.
  canCustomizeColumns?: boolean;
  // Fetches up to `limit` rows for export using the active filters/sort.
  // Required for the export dialog to do anything beyond the current page.
  fetchResultsForExport?: (limit: number) => Promise<CompanySearchResult[]>;
  // Called after an employee enrich with the fresh per-company counts so the
  // parent can overlay them on the displayed rows (mv_search can't be written
  // per-click). Keyed by euId → latest-year total.
  onEmployeeCountsUpdated?: (counts: Record<string, number>) => void;
  isLoading?: boolean;
  onSortChange?: (sort: string) => void;
  onOffsetChange?: (offset: number) => void;
  // Change the page size (results per page). Wired to the results-per-page
  // dropdown in the bottom pagination bar.
  onLimitChange?: (limit: number) => void;
  onRefresh?: () => void;
  // Fired when the export button is clicked — for every user (incl. guests and
  // non-entitled users), before the Pro check. Used for usage analytics.
  onExportClick?: () => void;
  // Fired after an export file is generated successfully, with what was
  // exported so the parent can log it alongside the active filters.
  onExportSuccess?: (info: { exportCount: number; columns: string[] }) => void;
  // The search's country scope ('DE' | 'CH' | 'AT'; undefined = global). Swiss
  // companies carry no financials, industry or contact data, so 'CH' swaps
  // in a default column set built from what the Swiss register does have.
  country?: string;
}

export default function CompanyResultsTable({
  results,
  total,
  totalCapped = false,
  offset,
  limit = 50,
  sort,
  financialYear,
  canExport = false,
  canEnrich = false,
  canCustomizeColumns = false,
  fetchResultsForExport,
  onEmployeeCountsUpdated,
  isLoading = false,
  onSortChange,
  onOffsetChange,
  onLimitChange,
  onRefresh,
  onExportClick,
  onExportSuccess,
  country,
  previewEuId,
  onOpenPreview,
}: CompanyResultsTableProps) {
  const { t, i18n } = useTranslation();
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);
  const totalLabel =
    fmt.fullNumber.format(total) + (totalCapped ? t('search.totalCappedSuffix') : '');
  const { locale } = useLocale();
  const { getOption, storeOption } = useSettings();
  const [isEnrichingWebsites, setIsEnrichingWebsites] = useState(false);
  const [isEnrichingEmployees, setIsEnrichingEmployees] = useState(false);
  const [showProDialog, setShowProDialog] = useState(false);
  // Which feature triggered the Pro prompt — drives the dialog's description.
  const [proDialogFeature, setProDialogFeature] = useState<
    'export' | 'columns' | 'enrich'
  >('export');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  // Row selection, keyed by eu_id so it survives paging and re-sorting.
  // Deliberately NOT cleared when the page changes: picking three companies
  // on page 1 and two on page 2 before adding them to a list is the whole
  // point of selecting across a result set.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedEuIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  const openProDialog = useCallback(
    (feature: 'export' | 'columns' | 'enrich') => {
      setProDialogFeature(feature);
      setShowProDialog(true);
    },
    [],
  );

  const handleExportClick = useCallback(() => {
    // Log every click — including guests and non-entitled users — before the
    // Pro gate, so we can see demand for the export feature.
    onExportClick?.();
    if (!canExport) {
      openProDialog('export');
      return;
    }
    setExportError(false);
    setShowExportDialog(true);
  }, [canExport, onExportClick, openProDialog]);

  const runExport = useCallback(
    async (columnIds: string[], count: number) => {
      setExportError(false);
      setIsExporting(true);
      try {
        // Fall back to the current page if no fetcher was provided.
        const rows = fetchResultsForExport
          ? await fetchResultsForExport(count)
          : results.slice(0, count);
        await exportToExcel(rows, columnIds, t, financialYear, country);
        onExportSuccess?.({ exportCount: rows.length, columns: columnIds });
        setShowExportDialog(false);
      } catch {
        setExportError(true);
      } finally {
        setIsExporting(false);
      }
    },
    [fetchResultsForExport, results, financialYear, t, onExportSuccess, country],
  );

  // Column visibility. Only Pro/Admin may customize columns, so only they get
  // their persisted choice merged over the defaults; everyone else always sees
  // the defaults. `name`/`index` are forced visible so a stale stored value can
  // never hide the anchor columns. Defaults and the stored choice are both
  // per country scope: Swiss (CH) and Austrian (AT) each have their own stored
  // choice, DE and Global share. AT starts from the Swiss defaults — like the
  // Swiss register, the Firmenbuch import carries no financials yet.
  const isSwiss = country === 'CH';
  const isAustrian = country === 'AT';
  const visibilitySetting = isAustrian
    ? VISIBLE_COLUMNS_SETTING_AT
    : isSwiss
      ? VISIBLE_COLUMNS_SETTING_CH
      : VISIBLE_COLUMNS_SETTING;
  const defaultVisibility = isSwiss || isAustrian
    ? SWISS_DEFAULT_COLUMN_VISIBILITY
    : DEFAULT_COLUMN_VISIBILITY;
  const storedVisibility = getOption<VisibilityState | undefined>(
    visibilitySetting,
  );
  const columnVisibility = useMemo<VisibilityState>(() => {
    const base = canCustomizeColumns
      ? { ...defaultVisibility, ...(storedVisibility ?? {}) }
      : { ...defaultVisibility };
    return { ...base, name: true, index: true };
  }, [canCustomizeColumns, storedVisibility, defaultVisibility]);

  const handleColumnVisibilityChange = useCallback(
    (id: string, visible: boolean) => {
      storeOption(visibilitySetting, {
        ...columnVisibility,
        [id]: visible,
      });
    },
    [columnVisibility, storeOption, visibilitySetting],
  );

  const handleSort = useCallback(
    (field: string) => {
      if (!onSortChange) return;

      const toggleFields = ['profit'];
      let newSort: string;
      if (sort === `${field}-desc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-asc`
          : 'revenue-desc';
      } else if (sort === `${field}-asc`) {
        newSort = toggleFields.includes(field)
          ? `${field}-desc`
          : 'revenue-desc';
      } else {
        newSort = `${field}-desc`;
      }
      onSortChange(newSort);
    },
    [onSortChange, sort],
  );

  const handleEnrichWebsites = useCallback(async () => {
    if (!canEnrich) {
      openProDialog('enrich');
      return;
    }
    const euIds = results.filter((r) => !r.domain).map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingWebsites(true);
    try {
      await backfillWebsiteBatchAction(euIds);
      onRefresh?.();
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingWebsites(false);
    }
  }, [results, canEnrich, openProDialog, onRefresh]);

  const handleEnrichEmployees = useCallback(async () => {
    if (!canEnrich) {
      openProDialog('enrich');
      return;
    }
    const euIds = results.map((r) => r.euId);
    if (euIds.length === 0) return;
    setIsEnrichingEmployees(true);
    try {
      const res = await backfillEmployeesBatchAction(euIds);
      // Overlay the freshly-parsed counts onto the displayed rows. We don't
      // call onRefresh here — that would re-read the (still stale) mv_search.
      if (res.success && res.results) {
        const counts: Record<string, number> = {};
        for (const r of res.results) {
          if (r.status !== 'ok' || !r.employee_data?.length) continue;
          // Latest year wins, matching how mv_search picks employee_count.
          const latest = r.employee_data
            .filter((d) => d.gesamt != null)
            .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0];
          if (latest?.gesamt != null) counts[r.eu_id] = latest.gesamt;
        }
        onEmployeeCountsUpdated?.(counts);
      }
    } catch {
      // silently handle enrich errors
    } finally {
      setIsEnrichingEmployees(false);
    }
  }, [results, canEnrich, openProDialog, onEmployeeCountsUpdated]);

  const columns = useMemo<ColumnDef<CompanySearchResult>[]>(() => {
    // Columns to hide on mobile — only Name and City are shown by default.
    const MOBILE_HIDE = new Set([
      'legalStatus',
      'bundesland',
      'rechtsform',
      'industrySector',
      'naceCode',
      'cpvCodes',
      'employees',
      'totalRevenue',
      'profit',
      'domain',
      'email',
      'registerType',
      'registerId',
      'registerCourt',
      'kreis',
      'postalCode',
    ]);
    const defs: ColumnDef<CompanySearchResult>[] = [
      {
        id: 'select',
        header: ({ table }) => (
          <SelectAllCheckbox
            t={t}
            table={table}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={t('search.selectRow', 'Select company')}
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(value === true)}
            // The row itself navigates to the company page.
            onClick={(e) => e.stopPropagation()}
          />
        ),
        size: 40,
        enableResizing: false,
      },
      {
        id: 'index',
        header: () => (
          <span className="text-muted-foreground w-8 block text-center">#</span>
        ),
        cell: (info) => (
          <span className="text-muted-foreground w-8 block text-center text-xs">
            {offset + info.row.index + 1}
          </span>
        ),
        size: 50,
        enableResizing: false,
      },
      {
        accessorKey: 'name',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('name')}
          >
            <span>{t('search.table.name')}</span>
            <SortIcon field="name" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const company = info.row.original;
          const faviconUrl = (
            company as CompanySearchResult & { faviconUrl?: string }
          ).faviconUrl;
          const slug = generateCompanySlug(
            {
              name: company.name,
              sitz: company.city,
              registerCourt: company.registerCourt,
              registerType: company.registerType,
              registerId: company.registerId,
            },
            locale,
          );
          return (
            <CompanyNameLink
              slug={slug}
              results={results}
              company={company}
              offset={offset}
              onOpenPreview={onOpenPreview}
              faviconUrl={faviconUrl}
              info={info}
            />
          );
        },
        size: 100,
      },
      {
        accessorKey: 'legalStatus',
        header: () => <span>{t('search.table.legalStatus', 'Status')}</span>,
        cell: (info) => {
          const status = info.getValue() as string;
          if (!status || status === 'active') {
            return (
              <span className={secondaryTextClass}>
                {t('search.sidebar.legalStatusOptions.active', {
                  defaultValue: 'Active',
                })}
              </span>
            );
          }
          return <LegalStatusBadge status={status} />;
        },
        size: 110,
      },
      {
        accessorKey: 'city',
        header: () => <span>{t('search.table.city')}</span>,
        // Flag before the city: with the country scope on Global, "Cham"
        // alone could be the one in Bavaria or the one in Zug.
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              className={`${secondaryTextClass} flex min-w-0 items-center gap-1.5 max-w-[100px] lg:max-w-[150px]`}
            >
              <CompanyCountryFlag
                countryCode={companyCountryCode(row.registerType, row.euId)}
              />
              <span className="truncate">
                {(info.getValue() as string) || '—'}
              </span>
            </span>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'bundesland',
        header: () => (
          <span>
            {t(`search.table.${columnLabelKey('bundesland', 'state', country)}`)}
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          const region = info.getValue() as string | null;
          const label = companyCountryCode(row.registerType, row.euId) === 'CH'
            ? swissCantonLabel(region, i18n.language)
            : region;
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[100px] lg:max-w-[150px]`}
              title={label || undefined}
            >
              {label || '—'}
            </span>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'rechtsform',
        header: () => (
          <span>{t('search.table.rechtsform', 'Legal form')}</span>
        ),
        cell: (info) => {
          const val = info.getValue() as string | null;
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[180px]`}
              title={val || undefined}
            >
              {val || '—'}
            </span>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'industrySector',
        header: () => <span>{t('search.table.industry')}</span>,
        cell: (info) => {
          const val = info.getValue() as string;
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[200px]`}
              title={val}
            >
              {val || '—'}
            </span>
          );
        },
        size: 150,
      },
      {
        accessorKey: 'naceCode',
        header: () => <span>{t('search.table.naceCode')}</span>,
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: 'employees',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('employees')}
          >
            <span>{t('search.table.employees')}</span>
            <SortIcon field="employees" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val === null || val === undefined)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <span className={secondaryTextClass}>
              {numberFormatter.format(val)}
            </span>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'totalRevenue',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('revenue')}
          >
            <span>{t('search.table.revenue')}</span>
            <SortIcon field="revenue" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val === null || val === undefined)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <span className={`${secondaryTextClass} whitespace-nowrap`}>
              {fmt.compactCurrency.format(val)}
            </span>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'profit',
        header: () => (
          <div
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            onClick={() => handleSort('profit')}
          >
            <span>{t('search.table.profit')}</span>
            <SortIcon field="profit" sort={sort} />
          </div>
        ),
        cell: (info) => {
          const val = info.getValue() as number | null;
          if (val === null || val === undefined)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <span className={secondaryTextClass}>
              {fmt.compactCurrency.format(val)}
            </span>
          );
        },
        size: 110,
      },
      {
        accessorKey: 'domain',
        header: () => <span>{t('search.table.website')}</span>,
        cell: (info) => {
          const val = info.getValue() as string | null;
          if (!val)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <a
              href={`https://${val}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[120px] lg:max-w-40"
              title={val}
              onClick={(e) => e.stopPropagation()}
            >
              {val}
            </a>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'email',
        header: () => <span>{t('search.table.email')}</span>,
        cell: (info) => {
          const val = info.getValue() as string | null;
          if (!val)
            return <span className="text-muted-foreground text-xs">—</span>;
          return (
            <a
              href={`mailto:${val}`}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[120px] lg:max-w-[180px]"
              title={val}
              onClick={(e) => e.stopPropagation()}
            >
              {val}
            </a>
          );
        },
        size: 150,
      },
      {
        accessorKey: 'registerType',
        header: () => <span>{t('search.table.registerType')}</span>,
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 110,
      },
      {
        accessorKey: 'registerId',
        header: () => (
          <span>
            {t(
              `search.table.${columnLabelKey('registerId', 'registerId', country)}`,
            )}
          </span>
        ),
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 120,
      },
      {
        accessorKey: 'registerCourt',
        header: () => (
          <span>
            {t(
              `search.table.${columnLabelKey('registerCourt', 'registerCourt', country)}`,
            )}
          </span>
        ),
        cell: (info) => {
          const row = info.row.original;
          const region = info.getValue() as string | null;
          const label = companyCountryCode(row.registerType, row.euId) === 'CH'
            ? swissCantonLabel(region, i18n.language)
            : region;
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[160px]`}
              title={label || undefined}
            >
              {label || '—'}
            </span>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'kreis',
        header: () => <span>{t('search.table.kreis')}</span>,
        cell: (info) => (
          <span
            className={`${secondaryTextClass} truncate block max-w-[120px] lg:max-w-[160px]`}
            title={(info.getValue() as string) || undefined}
          >
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 140,
      },
      {
        accessorKey: 'postalCode',
        header: () => <span>{t('search.table.postalCode')}</span>,
        cell: (info) => (
          <span className={`${secondaryTextClass} whitespace-nowrap`}>
            {(info.getValue() as string) || '—'}
          </span>
        ),
        size: 90,
      },
      {
        accessorKey: 'cpvCodes',
        header: () => <span>{t('search.table.cpvCodes')}</span>,
        cell: (info) => {
          const val = info.getValue() as string[] | null;
          const text = val?.length ? val.join(', ') : '';
          return (
            <span
              className={`${secondaryTextClass} truncate block max-w-[140px] lg:max-w-[200px]`}
              title={text || undefined}
            >
              {text || '—'}
            </span>
          );
        },
        size: 160,
      },
    ];
    return defs.map((c) => {
      const key = (c as { accessorKey?: string }).accessorKey;
      if (!key || !MOBILE_HIDE.has(key)) return c;
      const existingMeta =
        (c as { meta?: Record<string, unknown> }).meta ?? {};
      return {
        ...c,
        meta: {
          ...existingMeta,
          headerClassName: cn(
            'hidden lg:table-cell',
            existingMeta.headerClassName as string | undefined,
          ),
          cellClassName: cn(
            'hidden lg:table-cell',
            existingMeta.cellClassName as string | undefined,
          ),
        },
      };
    });
  }, [offset, sort, t, i18n.language, fmt, handleSort, country, locale, results, onOpenPreview]);

  // Show up to the current page size (rows-per-page dropdown can raise it
  // above the old fixed 50).
  const displayedResults = useMemo(
    () => results.slice(0, limit),
    [results, limit],
  );
  // Mobile shows 10 cards per page. Slice independently so the brief window
  // before the mobile-limit-override useEffect fires (SSR limit is 50) doesn't
  // flash 50 cards.
  const mobileResults = useMemo(
    () => results.slice(0, MOBILE_PAGE_SIZE),
    [results],
  );

  return (
    <div className="flex flex-col h-full w-full min-w-0 bg-background lg:overflow-hidden">
      {/* Toolbar (columns / export / enrich) is desktop-only — hidden on mobile
          so the export and enrichment actions don't show there. */}
      <ResultsToolbar
        t={t}
        country={country}
        columnVisibility={columnVisibility}
        handleColumnVisibilityChange={handleColumnVisibilityChange}
        canCustomizeColumns={canCustomizeColumns}
        openProDialog={openProDialog}
        selectedEuIds={selectedEuIds}
        displayedResults={displayedResults}
        setRowSelection={setRowSelection}
        handleExportClick={handleExportClick}
        results={results}
        handleEnrichWebsites={handleEnrichWebsites}
        isEnrichingWebsites={isEnrichingWebsites}
        handleEnrichEmployees={handleEnrichEmployees}
        isEnrichingEmployees={isEnrichingEmployees}
      />

      <div className="flex-1 min-h-0 min-w-0 relative lg:overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Desktop: rich DataGrid table */}
            <div className="hidden lg:block h-full">
              <TableImplementation
                previewEuId={previewEuId}
                onOpenPreview={onOpenPreview}
                data={displayedResults}
                columns={columns}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                total={total}
                totalLabel={totalLabel}
                locale={locale}
                offset={offset}
                limit={limit}
                onOffsetChange={onOffsetChange}
                onLimitChange={onLimitChange}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
              />
            </div>
            {/* Mobile: list of cards. The page itself scrolls (body overflow
                is only locked on desktop), so this branch flows naturally —
                no inner scroll container with a height it can't compute.
                Cards include the first line of the AI summary when available;
                pagination sits below the list. */}
            <MobileResultsList
              mobileResults={mobileResults}
              t={t}
              onOffsetChange={onOffsetChange}
              total={total}
              offset={offset}
              totalLabel={totalLabel}
            />
          </>
        )}
      </div>

      <ProFeatureDialog
        showProDialog={showProDialog}
        setShowProDialog={setShowProDialog}
        proDialogFeature={proDialogFeature}
        t={t}
      />

      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        total={total}
        totalLabel={totalLabel}
        t={t}
        isExporting={isExporting}
        exportError={exportError}
        onExport={runExport}
        country={country}
      />
    </div>
  );
}
